import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import styles from './AddUserForm.module.css';
import InputWithLabel from 'components/InputWithLabel/InputWithLabel';
import Button from 'components/Button/Button';
import { addUserSchema, AddUserFormValues } from '../../types/userSchemas';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUserPlus,
    faUserGraduate,
    faUser,
    faAt,
    faEnvelope,
    faLock,
    faPhone,
    faArrowRight,
    faBook,
    faLayerGroup,
    faIdCard
} from '@fortawesome/free-solid-svg-icons';
import { api } from 'api/axios';
import { useNavigate } from 'react-router';

const AddUserForm: React.FC = () => {
    const [selectedRole, setSelectedRole] = useState<'teacher' | 'student'>('student');
    const navigate = useNavigate();

    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors, isSubmitting },
    } = useForm<AddUserFormValues>({
        resolver: zodResolver(addUserSchema),
        defaultValues: {
            role: 'student',
        }
    });

    const handleRoleChange = (role: 'teacher' | 'student') => {
        setSelectedRole(role);
        setValue('role', role);
    };



    const onSubmit = async (data: AddUserFormValues) => {
        try {
            const finalData = { ...data };
            console.log('Adding User:', finalData);
            
            if (selectedRole === 'student') {
                await api.post('/students', finalData);
            }
            else {
                await api.post('/teachers', finalData);
            }
            alert('User added successfully!');
            navigate('/user-management');
        } catch (error: any) {
            console.error('Error adding user:', error);
            alert(error.response?.data?.message || 'Failed to add user');
        }
    };

    return (
        <div className={styles.pageContainer}>
            <div className={styles.formCard}>
                <header className={styles.header}>
                    <div className={styles.titleSection}>
                        <h1 className={styles.title}>Add New User</h1>
                        <p className={styles.subtitle}>Create a new account for your educational institution.</p>
                    </div>
                </header>

                <div className={styles.roleSelection}>
                    <span className={styles.roleLabel}>Select Role</span>
                    <div className={styles.roleTabs}>
                        <div
                            className={`${styles.roleTab} ${selectedRole === 'teacher' ? styles.activeRole : ''}`}
                            onClick={() => handleRoleChange('teacher')}
                        >
                            <FontAwesomeIcon icon={faUserGraduate} />
                            <span className={styles.roleTabText}>Teacher</span>
                        </div>
                        <div
                            className={`${styles.roleTab} ${selectedRole === 'student' ? styles.activeRole : ''}`}
                            onClick={() => handleRoleChange('student')}
                        >
                            <FontAwesomeIcon icon={faUser} />
                            <span className={styles.roleTabText}>Student</span>
                        </div>
                    </div>
                </div>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className={styles.grid}>


                        <InputWithLabel
                            label="Full Name"
                            placeholder="e.g. John Doe"
                            icon={<FontAwesomeIcon icon={faIdCard} />}
                            error={errors.full_name?.message}
                            {...register('full_name')}
                        />
                        <InputWithLabel
                            label="Username"
                            placeholder="e.g. johndoe"
                            icon={<FontAwesomeIcon icon={faAt} />}
                            error={errors.username?.message}
                            {...register('username')}
                        />

                        <div className={styles.fullWidth}>
                            <div className={styles.passwordSection}>
                                <InputWithLabel
                                    label="Password"
                                    type="password"
                                    placeholder="Enter strong password"
                                    icon={<FontAwesomeIcon icon={faLock} />}
                                    error={errors.password?.message}
                                    {...register('password')}
                                />
                                {/* <div className={styles.strengthBar}>
                                    <div className={styles.strengthFill}></div>
                                </div>
                                <span className={styles.strengthLabel}>Security Strength: Medium</span> */}
                            </div>
                        </div>

                        {selectedRole === 'student' && (
                            <>
                                <InputWithLabel
                                    label="Grade"
                                    placeholder="e.g. 10th"
                                    icon={<FontAwesomeIcon icon={faLayerGroup} />}
                                    error={(errors as any).grade?.message}
                                    {...register('grade')}
                                />
                                <InputWithLabel
                                    label="Section"
                                    placeholder="e.g. A"
                                    icon={<FontAwesomeIcon icon={faIdCard} />}
                                    error={(errors as any).section?.message}
                                    {...register('section')}
                                />
                            </>
                        )}

                    </div>

                    <div className={styles.actions}>
                        <Button
                            variant="primary"
                            type="submit"
                            className={styles.submitBtn}
                            isLoading={isSubmitting}
                        >
                            Submit <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: '10px' }} />
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddUserForm;
